import { ArrowDown, ArrowUp } from 'lucide-react' // Import missing icons
import { ArrowUpDown, Ban, CpuIcon, LogIn, MessageCircle, MoreHorizontal, Pencil, Shield, Trash2, UserCheck } from 'lucide-react'
import type { Column, ColumnDef } from '@tanstack/react-table'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import React from 'react'
import type { TFunction } from 'i18next' // Changed to type-only import
// Utility function for class names (assuming it exists in src/lib/utils.ts)
import { cn } from '@/lib/utils'
import type { useTranslation } from 'react-i18next'

// Define the shape of your data
export interface AdminUser {
	id: string
	name?: string | null
	email?: string | null
	username?: string | null
	displayUsername?: string | null
	role?: string | null
	roles?: string[] // From role .split(',')
	banned?: boolean
	// Add other fields you might need
}

export interface UserSession {
	id: string
	token: string
	userId: string
	expiresAt: string | Date | null
	ipAddress: string | null
	userAgent: string | null
	createdAt: string | Date
	updatedAt: string | Date
	impersonatedBy: string | null
}

interface UserColumnsProps {
	t: TFunction
	onSetRole: (user: AdminUser) => void
	onBan: (user: AdminUser) => void
	onUnban: (user: AdminUser) => void
	onDelete: (user: AdminUser) => void
	onImpersonate: (user: AdminUser) => void
	onManageSessions: (user: AdminUser) => void
	onSendNotification: (user: AdminUser) => void
}

export const getUserColumns = ({
	t,
	onSetRole,
	onBan,
	onUnban,
	onDelete,
	onImpersonate,
	onManageSessions,
	onSendNotification,
}: UserColumnsProps): ColumnDef<AdminUser>[] => [
	// Selection Column
	{
		id: 'select',
		header: ({ table }) => (
			<Checkbox
				checked={
					table.getIsAllPageRowsSelected()
						? true
						: table.getIsSomePageRowsSelected()
							? 'indeterminate'
							: false
				}
				onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
				aria-label={t('dataTable.selectAll')}
				className="translate-y-[2px]"
			/>
		),
		cell: ({ row }) => (
			<Checkbox
				checked={row.getIsSelected()}
				onCheckedChange={(value) => row.toggleSelected(!!value)}
				aria-label={t('dataTable.selectRow')}
				className="translate-y-[2px]"
			/>
		),
		enableSorting: false,
		enableHiding: false,
	},
	// ID Column
	{
		accessorKey: 'id',
		header: t('common.userId'),
		cell: ({ row }) => (
			<div className='w-[150px] truncate font-mono text-xs py-2 px-4'>{row.getValue('id')}</div>
		),
	},
	// Name Column
	{
		accessorKey: 'name',
		header: t('common.name'),
		cell: ({ row }) => (
			<div className='w-[180px] truncate py-2 px-4'>{row.getValue('name') || '-'}</div>
		),
	},
	// Email Column
	{
		accessorKey: 'email',
		header: t('common.email'),
		cell: ({ row }) => <div className='py-2 px-4'>{row.getValue('email') || '-'}</div>,
	},
	// Username Column
	{
		accessorKey: 'username',
		header: t('common.username'),
		cell: ({ row }) => <div className='py-2 px-4'>{row.getValue('username') || '-'}</div>,
	},
	// Display Username Column
	{
		accessorKey: 'displayUsername',
		header: t('common.displayUsername'),
		cell: ({ row }) => <div className='py-2 px-4'>{row.getValue('displayUsername') || '-'}</div>,
	},
	// Roles Column
	{
		accessorKey: 'roles',
		header: t('common.roles'),
		cell: ({ row }) => {
			const roles = row.original.roles || []
			if (!roles.length) return <div className='py-2 px-4'>-</div>

			return (
				<div className='flex flex-wrap gap-1.5 py-2 px-4'>
					{roles.map((role) => (
						<Badge key={role} variant={role === 'admin' ? 'destructive' : 'secondary'}>
							{role}
						</Badge>
					))}
				</div>
			)
		},
	},
	// Status Column
	{
		accessorKey: 'banned',
		header: t('common.status'),
		cell: ({ row }) => {
			const banned = row.getValue('banned')
			return (
				<div className='py-2 px-4'>
					{banned ? (
						<Badge variant='destructive'>{t('admin.users.columns.banned')}</Badge>
					) : (
						<Badge variant='default'>{t('admin.users.columns.active')}</Badge>
					)}
				</div>
			)
		},
	},
	// Actions Column
	{
		id: 'actions',
		cell: ({ row }) => {
			const user = row.original

			return (
				<div className='text-right py-2 px-4'>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' className='h-8 w-8 p-0'>
								<span className='sr-only'>{t('admin.users.columns.openMenu')}</span>
								<MoreHorizontal className='h-4 w-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end' className='w-48'>
							<DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
							<DropdownMenuItem onClick={() => onSetRole(user)}>
								<Shield className='mr-2 h-4 w-4' />
								{t('admin.users.columns.setRole')}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => onManageSessions(user)}>
								<CpuIcon className='mr-2 h-4 w-4' />
								{t('admin.users.columns.manageSessions')}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => onImpersonate(user)}>
								<LogIn className='mr-2 h-4 w-4' />
								{t('admin.users.columns.impersonateUser')}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => onSendNotification(user)}>
								<MessageCircle className='mr-2 h-4 w-4' />
								{t('admin.users.columns.sendNotification')}
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							{user.banned ? (
								<DropdownMenuItem onClick={() => onUnban(user)}>
									<UserCheck className='mr-2 h-4 w-4' />
									{t('admin.users.columns.unbanUser')}
								</DropdownMenuItem>
							) : (
								<DropdownMenuItem onClick={() => onBan(user)}>
									<Ban className='mr-2 h-4 w-4' />
									{t('admin.users.columns.banUser')}
								</DropdownMenuItem>
							)}
							<DropdownMenuSeparator />
							<DropdownMenuItem
								className='text-destructive focus:bg-destructive/10 focus:text-destructive'
								onClick={() => onDelete(user)}
							>
								<Trash2 className='mr-2 h-4 w-4' />
								{t('admin.users.columns.deleteUser')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			)
		},
	},
]

// Removed the DataTableColumnHeader component as it caused duplication.
// The DataTable component should handle rendering sortable headers automatically.
